<template>
  <div class="scenarios-section">
    <h2 class="section-title">{{ $t('ebook.scenarios.title') }}</h2>
    <div class="scenarios-grid">
      <div
        v-for="(scenario, index) in scenarios"
        :key="scenario.name"
        class="scenario-card"
        :class="`scenario-${index + 1}`"
      >
        <div class="scenario-image">
          <img :src="scenario.image" :alt="scenario.title" />
          <div class="scenario-icon-overlay">
            <Icon :name="scenario.icon" size="32" class="scenario-icon" />
          </div>
        </div>

        <div class="scenario-content">
          <h3 class="scenario-title">{{ scenario.title }}</h3>
          <p class="scenario-description">{{ scenario.description }}</p>

          <div class="scenario-examples">
            <div class="examples-header">
              <Icon name="material-symbols:lightbulb" size="16" />
              <span>{{ $t('ebook.scenarios.examplesHeader') }}</span>
            </div>
            <ul class="examples-list">
              <li v-for="example in scenario.examples" :key="example">
                <Icon name="material-symbols:check-small" size="14" />
                {{ example }}
              </li>
            </ul>
          </div>

          <div class="scenario-benefits">
            <div class="benefit-tag">
              <Icon name="material-symbols:trending-up" size="14" />
              <span>{{ scenario.benefit }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 适用场景数据（i18n）
const scenarios = computed(() => ([
  {
    name: 'foreign-reading',
    title: t('ebook.scenarios.items.0.title'),
    icon: 'material-symbols:translate',
    image: '/images/ebook/foreign-reading.jpg',
    description: t('ebook.scenarios.items.0.description'),
    examples: [
      t('ebook.scenarios.items.0.examples.0'),
      t('ebook.scenarios.items.0.examples.1'),
      t('ebook.scenarios.items.0.examples.2'),
      t('ebook.scenarios.items.0.examples.3')
    ],
    benefit: t('ebook.scenarios.items.0.benefit'),
    color: '#4A90E2'
  },
  {
    name: 'academic-research',
    title: t('ebook.scenarios.items.1.title'),
    icon: 'material-symbols:school',
    image: '/images/ebook/academic-research.jpg',
    description: t('ebook.scenarios.items.1.description'),
    examples: [
      t('ebook.scenarios.items.1.examples.0'),
      t('ebook.scenarios.items.1.examples.1'),
      t('ebook.scenarios.items.1.examples.2'),
      t('ebook.scenarios.items.1.examples.3')
    ],
    benefit: t('ebook.scenarios.items.1.benefit'),
    color: '#FF8C42'
  },
  {
    name: 'language-learning',
    title: t('ebook.scenarios.items.2.title'),
    icon: 'material-symbols:language',
    image: '/images/ebook/language-learning.jpg',
    description: t('ebook.scenarios.items.2.description'),
    examples: [
      t('ebook.scenarios.items.2.examples.0'),
      t('ebook.scenarios.items.2.examples.1'),
      t('ebook.scenarios.items.2.examples.2'),
      t('ebook.scenarios.items.2.examples.3')
    ],
    benefit: t('ebook.scenarios.items.2.benefit'),
    color: '#52C41A'
  },
  {
    name: 'collection-management',
    title: t('ebook.scenarios.items.3.title'),
    icon: 'material-symbols:collections-bookmark',
    image: '/images/ebook/collection-management.jpg',
    description: t('ebook.scenarios.items.3.description'),
    examples: [
      t('ebook.scenarios.items.3.examples.0'),
      t('ebook.scenarios.items.3.examples.1'),
      t('ebook.scenarios.items.3.examples.2'),
      t('ebook.scenarios.items.3.examples.3')
    ],
    benefit: t('ebook.scenarios.items.3.benefit'),
    color: '#9C27B0'
  }
]))
</script>

<style scoped lang="scss">
@use 'sass:color';

.scenarios-section {
  padding: $spacing-xxl 0;
  background: linear-gradient(135deg,
    rgba(#4A90E2, 0.05) 0%,
    rgba(#FF8C42, 0.05) 25%,
    rgba(#52C41A, 0.05) 50%,
    rgba(#9C27B0, 0.05) 75%,
    rgba(#4A90E2, 0.05) 100%);
}

.section-title {
  text-align: center;
  font-size: 2.2rem;
  font-weight: 700;
  color: $gray-900;
  margin: 0 0 $spacing-xl 0;

  background: linear-gradient(135deg, #4A90E2 0%, #FF8C42 50%, #52C41A 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: $spacing-xl;
  max-width: 1200px;
  margin: 0 auto;
}

.scenario-card {
  background: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-lg;
  box-shadow: 0 4px 20px rgba($black, 0.08);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    transition: height 0.3s ease;
  }

  &.scenario-1::before {
    background: linear-gradient(90deg, #4A90E2 0%, color.adjust(#4A90E2, $lightness: 15%) 100%);
  }

  &.scenario-2::before {
    background: linear-gradient(90deg, #FF8C42 0%, color.adjust(#FF8C42, $lightness: 15%) 100%);
  }

  &.scenario-3::before {
    background: linear-gradient(90deg, #52C41A 0%, color.adjust(#52C41A, $lightness: 15%) 100%);
  }

  &.scenario-4::before {
    background: linear-gradient(90deg, #9C27B0 0%, color.adjust(#9C27B0, $lightness: 15%) 100%);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba($black, 0.15);

    &::before {
      height: 6px;
    }

    .scenario-image img {
      transform: scale(1.05);
    }

    .scenario-icon-overlay .scenario-icon {
      transform: scale(1.2) rotate(5deg);
    }
  }

  &.scenario-1:hover {
    border-color: rgba(#4A90E2, 0.3);
  }

  &.scenario-2:hover {
    border-color: rgba(#FF8C42, 0.3);
  }

  &.scenario-3:hover {
    border-color: rgba(#52C41A, 0.3);
  }

  &.scenario-4:hover {
    border-color: rgba(#9C27B0, 0.3);
  }
}

.scenario-image {
  position: relative;
  margin-bottom: $spacing-lg;
  border-radius: $border-radius-lg;
  overflow: hidden;
  height: 200px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .scenario-icon-overlay {
    position: absolute;
    top: $spacing-md;
    right: $spacing-md;
    background: rgba($white, 0.9);
    border-radius: 50%;
    padding: $spacing-sm;
    backdrop-filter: blur(4px);

    .scenario-icon {
      transition: all 0.3s ease;
      filter: drop-shadow(0 2px 8px rgba($black, 0.1));
    }
  }
}

.scenario-card.scenario-1 .scenario-icon {
  color: #4A90E2;
}

.scenario-card.scenario-2 .scenario-icon {
  color: #FF8C42;
}

.scenario-card.scenario-3 .scenario-icon {
  color: #52C41A;
}

.scenario-card.scenario-4 .scenario-icon {
  color: #9C27B0;
}

.scenario-content {
  text-align: center;
}

.scenario-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: $gray-900;
  margin: 0 0 $spacing-md 0;
}

.scenario-description {
  font-size: 0.95rem;
  color: $gray-600;
  line-height: 1.6;
  margin: 0 0 $spacing-lg 0;
}

.scenario-examples {
  text-align: left;
  margin-bottom: $spacing-lg;

  .examples-header {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    font-size: 0.9rem;
    font-weight: 600;
    color: $gray-700;
    margin-bottom: $spacing-sm;

    .iconify {
      color: #FF8C42;
    }
  }

  .examples-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: flex-start;
      gap: $spacing-xs;
      font-size: 0.85rem;
      color: $gray-600;
      margin-bottom: $spacing-xs;
      line-height: 1.4;

      .iconify {
        color: #52C41A;
        margin-top: 2px;
        flex-shrink: 0;
      }
    }
  }
}

.scenario-benefits {
  display: flex;
  justify-content: center;

  .benefit-tag {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-sm $spacing-md;
    border-radius: $border-radius-lg;
    font-size: 0.8rem;
    font-weight: 500;
    transition: all 0.2s ease;
  }
}

.scenario-card.scenario-1 .benefit-tag {
  background: rgba(#4A90E2, 0.1);
  color: color.adjust(#4A90E2, $lightness: -15%);

  .iconify {
    color: #4A90E2;
  }
}

.scenario-card.scenario-2 .benefit-tag {
  background: rgba(#FF8C42, 0.1);
  color: color.adjust(#FF8C42, $lightness: -15%);

  .iconify {
    color: #FF8C42;
  }
}

.scenario-card.scenario-3 .benefit-tag {
  background: rgba(#52C41A, 0.1);
  color: color.adjust(#52C41A, $lightness: -15%);

  .iconify {
    color: #52C41A;
  }
}

.scenario-card.scenario-4 .benefit-tag {
  background: rgba(#9C27B0, 0.1);
  color: color.adjust(#9C27B0, $lightness: -15%);

  .iconify {
    color: #9C27B0;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .section-title {
    font-size: 1.8rem;
  }

  .scenarios-grid {
    grid-template-columns: 1fr;
    gap: $spacing-lg;
  }

  .scenario-card {
    padding: $spacing-md;
  }

  .scenario-title {
    font-size: 1.2rem;
  }

  .scenario-description {
    font-size: 0.9rem;
  }
}

// 卡片进入动画
.scenario-card {
  animation: fadeInUp 0.6s ease-out;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
